import {
  KPIDefinitionCommitDTO,
  KPIDefinitionCommitResultDTO,
  KPIDefinitionPreviewDTO,
  KPIDefinitionPreviewRequestDTO,
  KPIDefinitionRevisionInfoDTO,
  KPIDefinitionTextCorrectionMutationDTO,
  KPIStableMetadataMutationDTO,
} from 'models/dto/kpi/kpiDefinitionRevisionDTO';
import IdListDTO from 'models/dto/IdListDTO';
import KPI from 'models/kpi/kpi';
import {
  KPIDefinitionCommitResult,
  KPIDefinitionPreview,
  KPIDefinitionRevisionChoiceInfo,
  KPIDefinitionRevisionInfo,
  KPIDefinitionTextCorrectionMutation,
} from 'models/kpi/kpiDefinitionRevision';
import { mapFromKPI, mapToKPI } from 'mappings/kpiMapping';
import { mapFromTaskType } from 'mappings/taskTypeMapping';

export function mapToKPIDefinitionPreviewRequest(kpi: KPI, mutationId: string): KPIDefinitionPreviewRequestDTO {
  const output = new KPIDefinitionPreviewRequestDTO();
  output.kpi = mapToKPI(kpi);
  output.expectedKpiRowVersion = kpi.rowVersion;
  output.mutationId = mutationId;

  return output;
}

export function mapFromKPIDefinitionPreview(dto: KPIDefinitionPreviewDTO): KPIDefinitionPreview {
  const output = new KPIDefinitionPreview();
  output.previewToken = dto.previewToken;
  output.definitionHash = dto.definitionHash;
  output.kpiId = dto.kpiId;
  output.currentDefinitionRevisionId = dto.currentDefinitionRevisionId;
  output.affectedCatalogFamilyCount = dto.affectedCatalogFamilyCount;
  output.affectedPrivateOrManagedFamilyCount = dto.affectedPrivateOrManagedFamilyCount;
  output.affectedTaskCount = dto.affectedTaskCount;
  output.requiresConfirmation = dto.requiresConfirmation;
  output.affectedCatalogFamilyNames = dto.affectedCatalogFamilyNames;
  output.inaccessibleCatalogFamilyCount = dto.inaccessibleCatalogFamilyCount;
  output.compatibilityBlockers = dto.compatibilityBlockers;

  return output;
}

export function mapToKPIDefinitionCommit(
  kpi: KPI,
  previewToken: string,
  mutationId: string,
  reason: string | undefined,
  confirmed: boolean,
): KPIDefinitionCommitDTO {
  const output = new KPIDefinitionCommitDTO();
  output.kpi = mapToKPI(kpi);
  output.previewToken = previewToken;
  output.mutationId = mutationId;
  output.reason = reason;
  output.confirmed = confirmed;

  return output;
}

export function mapFromKPIDefinitionCommitResult(dto: KPIDefinitionCommitResultDTO): KPIDefinitionCommitResult {
  const output = new KPIDefinitionCommitResult();
  output.kpi = mapFromKPI(dto.kpi, undefined);
  output.kpiDefinitionRevisionId = dto.kpiDefinitionRevisionId;
  output.updatedTaskTypes = dto.updatedTaskTypes.map((taskType) => mapFromTaskType(taskType));

  return output;
}

export function mapFromKPIDefinitionRevisionInfos(dtos: KPIDefinitionRevisionInfoDTO[]): KPIDefinitionRevisionInfo[] {
  return dtos.map((dto) => {
    const output = new KPIDefinitionRevisionInfo();
    output.kpiId = dto.kpiId;
    output.kpiDefinitionRevisionId = dto.kpiDefinitionRevisionId;
    output.revision = dto.revision;
    output.isCurrent = dto.isCurrent;
    output.type = dto.type;
    output.minValue = dto.minValue;
    output.maxValue = dto.maxValue;
    output.decimalCount = dto.decimalCount;
    output.defValue = dto.defValue;
    output.required = dto.required;
    output.attachmentMode = dto.attachmentMode;
    output.commentMode = dto.commentMode;
    output.showLabel = dto.showLabel;
    output.source = dto.source;
    output.actorId = dto.actorId;
    output.created = new Date(dto.created);
    output.name = dto.name;
    output.description = dto.description;
    output.instruction = dto.instruction;
    output.expectedResult = dto.expectedResult;
    output.choices = (dto.choices ?? []).map((choiceDto) => {
      const choice = new KPIDefinitionRevisionChoiceInfo();
      choice.choiceKey = choiceDto.choiceKey;
      choice.sortOrder = choiceDto.sortOrder;
      choice.value = choiceDto.value;
      choice.description = choiceDto.description;

      return choice;
    });

    return output;
  });
}

export function mapToKPIStableMetadataMutation(kpi: KPI, mutationId: string): KPIStableMetadataMutationDTO {
  const output = new KPIStableMetadataMutationDTO();
  output.kpiId = kpi.kpiId;
  output.isField = kpi.isField;
  output.authSchemaId = kpi.authSchemaId;
  output.tagIds = new IdListDTO(kpi.tagIds.map((id) => id.toString()));
  output.mutationId = mutationId;

  return output;
}

export function mapToKPIDefinitionTextCorrectionMutation(
  mutation: KPIDefinitionTextCorrectionMutation,
  mutationId: string,
): KPIDefinitionTextCorrectionMutationDTO {
  const output = new KPIDefinitionTextCorrectionMutationDTO();
  output.kpiId = mutation.kpiId;
  output.kpiDefinitionRevisionId = mutation.kpiDefinitionRevisionId;
  output.choiceKey = mutation.choiceKey;
  output.languageId = mutation.languageId;
  output.name = mutation.name;
  output.description = mutation.description;
  output.instruction = mutation.instruction;
  output.expectedResult = mutation.expectedResult;
  output.choiceValue = mutation.choiceValue;
  output.question = mutation.question;
  output.expectedDefinitionRevisionRowVersion = mutation.expectedDefinitionRevisionRowVersion;
  output.expectedImpactHash = mutation.expectedImpactHash;
  output.mutationId = mutationId;

  return output;
}
